import jsCookie from 'js-cookie';
import { useRouter } from 'next/router';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { trackGAEvent } from 'utils/analytics';

import { IAuth } from '@/modules/auth/types';
import { REDIRECT_AFTER_LOGIN } from '@/shared/constant/roles-and-permissions';
import routes, { publicRoutes } from '@/shared/constant/routes';
import { IAuthContext } from '@/shared/hooks/context/auth/IAuthContext';
import { IGeneral } from '@/shared/types';

import AuthContext from './context';

export interface AuthState {
  authenticated: boolean;
  checked: boolean;
  token: string | null;
  user: IAuth.IUser | null;
  userLoading: boolean;
  collapseState: boolean | null;
  onboardingChecks: IGeneral.IGetOnboardingStatusResponse | null;
  subscriptionType: 'Enterprise' | 'Operations' | 'Business' | null;
}
// const NEXT_PUBLIC_REMEMBER_TOKEN_COOKIES_AGE = 30;
// const NEXT_PUBLIC_TOKEN_COOKIES_AGE = 0;

/* -------------------------------------------------------------------------- */
/* -------------------------------------------------------------------------- */
/* -------------------------------------------------------------------------- */
/*                          THIS FILE IS CHALLENGING                          */
/* -------------------------------------------------------------------------- */
/* -------------------------------------------------------------------------- */
/* -------------------------------------------------------------------------- */

const AuthProvider: FC<IAuthContext.IAuthProviderProps> = ({
  children,
  ...props
}) => {
  const router = useRouter();

  const [data, setData] = useState<AuthState>({
    authenticated: false,
    checked: false,
    token: null,
    user: null,
    userLoading: true,
    collapseState: true,
    subscriptionType: null,
    onboardingChecks: null,
  });

  useEffect(() => {
    /* --------------- Checking if the user is logged in already! --------------- */

    const token = jsCookie.get('token');
    const userObj = jsCookie.get('userObj');
    const user = JSON.parse(userObj ?? '{}')?.value;

    if (token) {
      if ('value' in JSON.parse(userObj ?? '{}')) {
        setData((prevData) => ({
          ...prevData,
          user: user ?? '',
          token,
          authenticated: true,
          checked: true,
          userLoading: false,
          // collapseState: tempCollapseState ? JSON.parse(tempCollapseState) : null,
        }));
      } else {
        logout();
      }
    } else {
      setData((prevData) => ({
        ...prevData,
        token: null,
        authenticated: false,
        checked: true,
        userLoading: false,
        collapseState: null,
      }));
      // go to login
      // jsCookie.set('redirect', router.asPath);
      if (!publicRoutes.includes(router.pathname)) router.push(routes.login);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = (
    token: string,
    user: IAuth.IUser | null = null,
    expiry: number,
    redirectEnabled = true
  ) => {
    // TODO : set Expiration (in .env )
    // const expiry = Number(NEXT_PUBLIC_TOKEN_COOKIES_AGE || 1);
    // from ms to
    const expires = expiry ? expiry / (1000 * 60 * 60 * 24) : undefined;
    if (token) {
      jsCookie.set('token', token, {
        path: '/',
        // note :when expires undefined that main that it's age = (session)
        sameSite: 'Strict',
        expires,
      });
    }
    if (user) {
      jsCookie.set(
        'userObj',
        JSON.stringify({
          value: user,
          expires,
        }),
        {
          path: '/',
          sameSite: 'Strict',
          expires,
        }
      );
    }
    setData({ ...data, token, authenticated: true, user });

    // we use it when user have a link but not loged in yet so redirect him to login then back him to the page
    const redirect = jsCookie.get('redirect');

    if (redirect) jsCookie.remove('redirect');

    // TODO: redirect
    // if there are afterLoginTo then take it as redirect else check redirect permissions else redirect to index
    const href =
      props.afterLoginTo ||
      (user?.companyUser === 0 && routes.createCompany) ||
      (user?.userGroupId && REDIRECT_AFTER_LOGIN[user?.userGroupId]) ||
      routes.home;

    if (token && user && redirectEnabled) {
      router.replace(redirect ? redirect : href, undefined, {
        locale: router.locale,
      });
      trackGAEvent({
        userName: `${user.firstName} ${user.lastName}`,
        companyId: user.companyId,
      });
    }
  };

  const logout = useCallback(() => {
    jsCookie.remove('token', { path: '/' });
    jsCookie.remove('userObj', { path: '/' });
    sessionStorage.clear();
    // window.sessionStorage.removeItem('sideMenuCollapeState');

    setData({
      ...data,
      token: '',
      user: null,
      authenticated: false,
      userLoading: false,
    });

    const customHref = props?.afterLogoutTo;
    router.push(customHref || `/login`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, props, router]);

  const updateUser = (user: Partial<IAuth.IUser> | null) => {
    const userObj = jsCookie.get('userObj');
    const expires = JSON.parse(userObj ?? '{}')?.expires;

    if (user) {
      setData((prev) => {
        const newUser = prev?.user
          ? {
              ...prev.user,
              ...user,
            }
          : null;
        if (newUser)
          jsCookie.set(
            'userObj',
            JSON.stringify({
              value: newUser,
              expires,
            }),
            { expires }
          );
        return {
          ...prev,
          user: newUser,
        };
      });
    }
  };

  const updateSubscriptionType = (
    subscription: IAuth.IUserSubscription | null
  ) => {
    if (subscription === null) {
      setData((prev) => ({
        ...prev,
        subscriptionType: null,
      }));
    } else {
      const isBusiness: boolean = subscription?.bundleName.includes('Business');
      const isOperations: boolean =
        subscription?.bundleName.includes('Operations');
      const isEnterprise: boolean =
        subscription?.bundleName.includes('Enterprise');

      setData((prev) => ({
        ...prev,
        subscriptionType: isOperations
          ? 'Operations'
          : isBusiness
          ? 'Business'
          : isEnterprise
          ? 'Enterprise'
          : null,
      }));
    }
  };

  const updateCollapseState = (collapseState: boolean | null) => {
    setData((prev) => ({
      ...prev,
      collapseState,
    }));
    // comment this because we need expire date if we didn't make will be as session expire
    // jsCookie.set('userObj', JSON.stringify(user));
  };
  /**
   * @description useful for buttons that require auth, it will take the user to login page and redirect him back to provided path on success
   */
  const comebackLogin = (
    config?: IAuthContext.IRedirectConfig & IAuthContext.IComeBackConfig
  ) => {
    const href = `${config?.comebackTo || router.asPath}`;

    jsCookie.set('redirect', href);

    const customHref = config?.redirectTo || props.loginPath || `/login`;

    router.push(customHref);
  };

  const updateOnboardingChecks = (
    checks: IGeneral.IGetOnboardingStatusResponse | null
  ) => {
    setData((prevData) => ({
      ...prevData,
      onboardingChecks: checks,
    }));
  };
  // const [error, setError] = useState(false);

  // TODO ::  Add API Endpoint to get user
  // useEffect(() => {
  //   const getUser = async () => {
  //     if (data.authenticated && !data.user && !error) {
  //       try {
  //         const { data: response } = await axiosClient.get(`get user here`);
  //         if (response.error) {
  //           setError(true);
  //           logout();
  //           return;
  //         }
  //         updateUser(response.data);
  //       } catch (e: unknown) {
  //         setError(true);
  //       }
  //     }
  //   };
  //   getUser();
  // }, [updateUser, error, logout, data]);

  // const contextValue = useMemo(
  //   () => ({
  //     ...data,
  //     login,
  //     logout,
  //     updateUser,
  //     comebackLogin,
  //     settings: props
  //   }),
  //   []
  // );

  return (
    <AuthContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        ...data,
        login,
        logout,
        updateUser,
        comebackLogin,
        settings: props,
        updateCollapseState,
        updateSubscriptionType,
        updateOnboardingChecks,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
